import styled from 'styled-components';
import { motion } from 'framer-motion';

export const Container = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => `linear-gradient(135deg, ${props.theme.colors.background} 0%, ${props.theme.colors.secondaryBackground} 100%)`};
  position: relative;
  overflow: hidden;
  
  /* Simple animated background without pseudo-elements */
  background-image: 
    radial-gradient(circle at 20% 80%, ${props => props.theme.colors.primary}08 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, ${props => props.theme.colors.primary}06 0%, transparent 50%);
  /* Remove animation to prevent flickering */
  background-size: 100% 100%, 100% 100%;
`;

export const Header = styled.div`
  padding: 12px 16px;
  background: ${props => `linear-gradient(135deg, ${props.theme.colors.headerBackground}ee 0%, ${props.theme.colors.headerBackground}dd 100%)`};
  backdrop-filter: blur(20px);
  border-bottom: 1px solid ${props => props.theme.colors.border}33;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 30;
  box-shadow: 0 2px 20px ${props => props.theme.colors.primary}10;
  
  /* Simple glassmorphism without pseudo-elements */
  background-image: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
`;

export const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
  z-index: 1;

  img {
    width: 40px;
    height: 40px;
    border-radius: ${props => props.theme.borderRadius.message};
    object-fit: cover;
    box-shadow: 0 4px 12px ${props => props.theme.colors.primary}20;
    border: 2px solid ${props => props.theme.colors.primary}20;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
      transform: scale(1.05);
      box-shadow: 0 8px 25px ${props => props.theme.colors.primary}30;
    }
  }

  div {
    h2 {
      font-size: ${props => props.theme.fontSize.header};
      font-weight: 700;
      color: ${props => props.theme.colors.text};
      background: ${props => `linear-gradient(135deg, ${props.theme.colors.text} 0%, ${props.theme.colors.primary} 100%)`};
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      margin: 0;
      line-height: 1.2;
    }
    span {
      font-size: ${props => props.theme.fontSize.small};
      color: ${props => props.theme.colors.secondaryText};
      font-weight: 500;
      opacity: 0.8;
    }
  }
`;

export const ChatContainer = styled.div`
  flex: 1;
  padding: 24px 20px;
  padding-bottom: 80px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: transparent;
  position: relative;
  scroll-behavior: smooth;
  z-index: 1;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.primary}30;
    border-radius: 10px;
    transition: all 0.3s ease;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.primary}50;
  }
`;

export const MessageWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.sent ? 'flex-end' : 'flex-start'};
  gap: 6px;
  max-width: 85%;
  ${props => props.sent ? 'margin-left: auto;' : ''}
  
  /* Animation */
  animation: slideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

export const MessageContent = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  width: fit-content;
  max-width: 100%;
  position: relative;
`;

export const Avatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  box-shadow: 0 4px 12px ${props => props.theme.colors.primary}15;
  border: 2px solid ${props => props.theme.colors.background};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 8px 25px ${props => props.theme.colors.primary}25;
  }
`;

export const Message = styled.div`
  padding: 16px 20px;
  border-radius: ${props => props.sent 
    ? '20px 20px 6px 20px' 
    : '20px 20px 20px 6px'};
  background: ${props => props.sent 
    ? `linear-gradient(135deg, ${props.theme.colors.userMessageBackground} 0%, ${props.theme.colors.userMessageBackground}dd 100%)`
    : `linear-gradient(135deg, ${props.theme.colors.botMessageBackground} 0%, ${props.theme.colors.botMessageBackground}f5 100%)`};
  box-shadow: ${props => props.sent 
    ? `0 8px 32px ${props.theme.colors.primary}20, 0 2px 8px ${props.theme.colors.primary}10`
    : `0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)`};
  max-width: 100%;
  position: relative;
  backdrop-filter: blur(10px);
  border: 1px solid ${props => props.sent 
    ? `${props.theme.colors.primary}20`
    : 'rgba(255,255,255,0.1)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${props => props.isQueued ? 0.6 : 1};
  
  /* Hover effect */
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.sent 
      ? `0 12px 40px ${props.theme.colors.primary}25, 0 4px 12px ${props.theme.colors.primary}15`
      : `0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)`};
  }
`;

export const UserMessageContent = styled.div`
  white-space: pre-wrap;
  word-wrap: break-word;
  color: ${props => props.userTextColor || props.theme.colors.userMessageText};
  line-height: 1.6;
  font-size: ${props => props.theme.fontSize.base};
  font-weight: 500;
  letter-spacing: 0.01em;
`;

export const Timestamp = styled.span`
  font-size: ${props => props.theme.fontSize.small};
  color: ${props => props.theme.colors.secondaryText};
  margin: 0 8px;
  padding-left: ${props => props.sent ? '0' : '48px'};
  opacity: 0.7;
  font-weight: 500;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
  }
`;

export const InputContainer = styled.div`
  padding: 24px 20px 28px;
  background: ${props => `linear-gradient(135deg, ${props.theme.colors.background}f8 0%, ${props.theme.colors.background}f0 100%)`};
  backdrop-filter: blur(25px);
  border-top: 1px solid ${props => props.theme.colors.border}25;
  display: flex;
  gap: 12px;
  align-items: flex-end;
  position: relative;
  z-index: 20;
  box-shadow: 
    0 -8px 32px rgba(0,0,0,0.08), 
    0 -2px 16px rgba(0,0,0,0.04),
    0 -1px 0 rgba(255,255,255,0.1);
  
  /* Simple glassmorphism without pseudo-elements */
  background-image: 
    linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 100%),
    linear-gradient(90deg, transparent 0%, ${props => props.theme.colors.primary}15 50%, transparent 100%);
  background-size: 100% 100%, 100% 1px;
  background-position: 0 0, 20px 0;
  background-repeat: no-repeat;
  
  /* Responsive design */
  @media (max-width: 768px) {
    padding: 20px 16px 24px;
    gap: 10px;
  }
`;

export const Input = styled.textarea`
  flex: 1;
  padding: 18px 24px;
  border: 1px solid ${props => props.theme.colors.border}40;
  border-radius: 28px;
  background: ${props => props.theme.colors.inputBackground};
  color: ${props => props.theme.colors.inputText};
  font-size: ${props => props.theme.fontSize.base};
  font-weight: 500;
  outline: none;
  resize: none;
  min-height: 28px;
  max-height: 120px;
  line-height: 1.5;
  overflow-y: auto;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
  
  /* Simple shadow */
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  
  /* Custom scrollbar - ẩn hoàn toàn */
  &::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }
  
  /* Cho Firefox */
  scrollbar-width: none;
  
  /* Cho IE */
  -ms-overflow-style: none;

  &:focus {
    border-color: ${props => props.theme.colors.primary}60;
    box-shadow: 0 0 0 2px ${props => props.theme.colors.primary}20, 0 4px 12px rgba(0,0,0,0.08);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${props => props.theme.colors.inputBackground}aa;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.secondaryText};
    opacity: 0.6;
    font-weight: 400;
    font-style: italic;
  }
  
  /* Simple hover effect */
  &:hover:not(:disabled):not(:focus) {
    border-color: ${props => props.theme.colors.border}60;
    box-shadow: 0 3px 10px rgba(0,0,0,0.08);
  }
  
  /* Responsive design */
  @media (max-width: 768px) {
    padding: 16px 20px;
    font-size: ${props => props.theme.fontSize.small};
    border-radius: 24px;
  }
`;

export const HeaderIconButton = styled(motion.button)`
  background: ${props => `linear-gradient(135deg, ${props.theme.colors.secondaryBackground}80 0%, ${props.theme.colors.secondaryBackground}60 100%)`};
  backdrop-filter: blur(10px);
  border: 1px solid ${props => props.theme.colors.border}40;
  padding: 10px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);

  &:hover {
    background: ${props => `linear-gradient(135deg, ${props.theme.colors.primary}20 0%, ${props.theme.colors.primary}10 100%)`};
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
    border-color: ${props => props.theme.colors.primary}60;
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    transition: all 0.2s ease;
  }
  
  &:hover svg {
    transform: scale(1.1);
  }
`;

export const SendButton = styled(motion.button)`
  min-width: 64px;
  height: 64px;
  border-radius: 50%;
  border: none;
  background: ${props => props.disabled 
    ? `linear-gradient(135deg, ${props.theme.colors.secondaryBackground}dd 0%, ${props.theme.colors.secondaryBackground}cc 100%)`
    : `linear-gradient(135deg, ${props.theme.colors.primary} 0%, ${props.theme.colors.primary}e6 100%)`};
  color: ${props => props.disabled 
    ? props.theme.colors.secondaryText 
    : '#ffffff'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  padding: 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.disabled 
    ? '0 4px 12px rgba(0,0,0,0.08)' 
    : `0 8px 32px ${props.theme.colors.primary}35, 0 4px 16px ${props.theme.colors.primary}25, inset 0 1px 0 rgba(255,255,255,0.2)`};
  opacity: 1;
  position: relative;
  z-index: 1;
  
  /* Enhanced gradient border effect without pseudo-elements */
  border: 2px solid transparent;
  background-image: ${props => props.disabled 
    ? 'none'
    : `linear-gradient(135deg, ${props.theme.colors.primary} 0%, ${props.theme.colors.primary}dd 100%), 
       linear-gradient(135deg, ${props.theme.colors.primary}60 0%, ${props.theme.colors.primary}30 50%, ${props.theme.colors.primary}60 100%)`};
  background-origin: border-box;
  background-clip: content-box, border-box;

  svg {
    width: 28px;
    height: 28px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    z-index: 2;
    filter: drop-shadow(0 1px 2px rgba(0,0,0,0.1));
  }

  &:hover:not(:disabled) {
    transform: translateY(-3px) scale(1.05);
    box-shadow: ${props => `0 12px 48px ${props.theme.colors.primary}40, 0 8px 24px ${props.theme.colors.primary}30, inset 0 1px 0 rgba(255,255,255,0.3)`};
    
    svg {
      transform: scale(1.1) rotate(-5deg);
    }
  }

  &:active:not(:disabled) {
    transform: translateY(-1px) scale(1.02);
    transition: all 0.1s ease;
  }
  
  &:disabled {
    transform: none;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    
    svg {
      opacity: 0.6;
    }
  }
  
  /* Loading state - removed rotation animation */
  &[data-loading="true"]:not(:disabled) {
    /* Keep button normal, no special animation */
  }
  
  /* Remove rotation keyframes */
  
  /* Responsive design */
  @media (max-width: 768px) {
    min-width: 52px;
    height: 52px;
    
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

export const ScrollButtonWrapper = styled.div`
  position: absolute;
  bottom: 100px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 10;
  pointer-events: none;
  
  /* Allow pointer events on children */
  > * {
    pointer-events: auto;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: ${props => `linear-gradient(135deg, ${props.theme.colors.background} 0%, ${props.theme.colors.secondaryBackground} 100%)`};
  position: relative;
  
  /* Simple animated background without pseudo-elements */
  background-image: 
    radial-gradient(circle at 30% 70%, ${props => props.theme.colors.primary}08 0%, transparent 50%),
    radial-gradient(circle at 70% 30%, ${props => props.theme.colors.primary}06 0%, transparent 50%);
  animation: backgroundPulse 3s ease-in-out infinite;
  
  @keyframes backgroundPulse {
    0%, 100% { 
      opacity: 1; 
      background-size: 100% 100%, 100% 100%;
    }
    50% { 
      opacity: 0.9; 
      background-size: 110% 110%, 110% 110%;
    }
  }
`;

export const LoadingContent = styled.div`
  padding: 32px;
  background: ${props => `linear-gradient(135deg, ${props.theme.colors.background}f8 0%, ${props.theme.colors.background}f0 100%)`};
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 
    0 16px 64px rgba(0,0,0,0.12), 
    0 4px 16px rgba(0,0,0,0.08),
    inset 0 1px 0 rgba(255,255,255,0.2);
  border: 1px solid rgba(255,255,255,0.15);
  text-align: center;
  position: relative;
  z-index: 1;
  
  /* Enhanced glassmorphism for better text visibility */
  background-image: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 100%);
  
  /* Loading animation */
  animation: fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px) scale(0.9);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;